import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { RefreshCw, Clock, Mail, CheckCircle2, XCircle } from "lucide-react";
import { usePageSEO } from "../hooks/usePageSEO";

const RefundPage = () => {
  usePageSEO();
  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fdf2f4] rounded-full mb-4">
              <RefreshCw className="w-8 h-8 text-[#b89a6d]" />
            </div>
            <h1 className="text-4xl font-bold text-[#3a2a26] mb-4">Refund Policy</h1>
            <p className="text-[#7a6b62]">Last updated: February 27, 2026</p>
          </div>

          <div className="space-y-8">
            {/* Overview */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">Overview</h2>
              <p className="text-[#5a4a40] leading-relaxed">
                We want you to be satisfied with your purchase. If you're not completely happy with your order, 
                we're here to help. Please review our refund policy below for details on returns and refunds.
              </p>
            </section>

            {/* Eligibility */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <CheckCircle2 className="w-6 h-6 text-[#b89a6d] mr-3" />
                Eligible for Refund
              </h2>
              <ul className="text-[#5a4a40] space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Unopened products in original packaging within 30 days of delivery</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Damaged or defective products (must notify within 7 days of receipt)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#b89a6d]">•</span>
                  <span>Incorrect items shipped (we'll provide a prepaid return label)</span>
                </li>
              </ul>
            </section>

            {/* Not Eligible */}
            <section className="bg-white border border-[#e8d4d0] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <XCircle className="w-6 h-6 text-[#c47267] mr-3" />
                Not Eligible for Refund
              </h2>
              <ul className="text-[#5a4a40] space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">•</span>
                  <span>Opened or used products (for hygiene and safety reasons)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">•</span>
                  <span>Products returned after 30 days from delivery date</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#c47267]">•</span>
                  <span>Products damaged due to misuse or improper storage</span>
                </li>
              </ul>
            </section>

            {/* Process */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Clock className="w-6 h-6 text-[#b89a6d] mr-3" />
                Refund Process
              </h2>
              <ol className="text-[#5a4a40] space-y-4">
                <li className="flex items-start gap-3">
                  <span className="bg-[#d4a5ad] text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                  <span><strong className="text-[#3a2a26]">Contact Us:</strong> Email everything@8eight8.net with your order number and reason for return.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#d4a5ad] text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                  <span><strong className="text-[#3a2a26]">Return Authorization:</strong> We'll review your request and provide return instructions within 2 business days.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#d4a5ad] text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">3</span>
                  <span><strong className="text-[#3a2a26]">Ship Product:</strong> Return the product in its original packaging to the provided address.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-[#d4a5ad] text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">4</span>
                  <span><strong className="text-[#3a2a26]">Refund Processing:</strong> Refunds are processed within 5-10 business days after we receive and inspect the return.</span>
                </li>
              </ol>
            </section>

            {/* Contact */}
            <section className="bg-white border border-[#f0d8d4] rounded-xl p-8">
              <h2 className="text-2xl font-bold text-[#3a2a26] mb-4 flex items-center">
                <Mail className="w-6 h-6 text-[#b89a6d] mr-3" />
                Questions?
              </h2>
              <p className="text-[#5a4a40] mb-4">
                If you have questions about our refund policy, please contact us:
              </p>
              <p className="text-[#5a4a40]">
                Email: <a href="mailto:everything@8eight8.net" className="text-[#b89a6d] hover:underline">everything@8eight8.net</a><br />
                Phone: <a href="tel:+18083012134" className="text-[#b89a6d] hover:underline">+1 (808) 301-2134</a>
              </p>
            </section>

            {/* Related Links */}
            <div className="flex flex-wrap gap-4 justify-center pt-4">
              <Link 
                to="/shipping" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Shipping Policy
              </Link>
              <Link 
                to="/terms" 
                className="px-6 py-3 bg-white border border-[#f0d8d4] rounded-xl text-[#b89a6d] hover:border-[#d4a5ad] transition-all"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPage;
